/**
 * Table structured data (JSON-LD) for comparison tables
 * Optimized for AEO (Answer Engine Optimization) and GEO (Generative Engine Optimization)
 * Helps answer engines extract tabular data directly
 * Follows Schema.org Table specification
 */

import React from 'react';
import { JSONLDScript } from './jsonld-script';

/**
 * A single table row
 */
export interface TableRow {
  /** Row cells (should match number of columns) */
  cells: string[];
}

/**
 * Table structured data props
 */
export interface TableStructuredDataProps {
  /** Table title/name */
  name: string;
  /** Description of what the table is about */
  about: string;
  /** Column headers */
  columns: string[];
  /** Table rows */
  rows: TableRow[];
  /** Optional URL where the table appears */
  url?: string;
}

/**
 * Table structured data component
 *
 * Generates Schema.org Table JSON-LD for comparison tables.
 * This helps AI-powered search engines extract tabular data directly
 * when users ask comparison questions.
 *
 * @example
 * ```tsx
 * <TableStructuredData
 *   name="Email Ferret vs SaneBox Feature Comparison"
 *   about="Comparison of features between Email Ferret and SaneBox email management tools"
 *   columns={['Feature', 'Email Ferret', 'SaneBox']}
 *   rows={[
 *     { cells: ['AI Cold Outreach Detection', 'Yes', 'No'] },
 *     { cells: ['Privacy (No Email Content Storage)', 'Yes', 'No'] },
 *     { cells: ['Starting Price', '$5/month', '$7/month'] },
 *   ]}
 *   url="https://emailferret.io/compare/sanebox"
 * />
 * ```
 *
 * @see https://schema.org/Table for complete schema specification
 */
export function TableStructuredData({ name, about, columns, rows, url }: TableStructuredDataProps) {
  const baseUrl =
    process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL || 'https://emailferret.io';
  const pageUrl = url || baseUrl;

  // Validate that all rows have the same number of cells as columns
  const columnCount = columns.length;
  const validRows = rows.filter((row) => row.cells.length === columnCount);

  if (validRows.length === 0) {
    // Don't render if no valid rows
    return null;
  }

  // Schema.org doesn't have a standard Table type, but we can use ItemList
  // to represent tabular data in a way that AI engines can parse
  // Each row becomes a ListItem with structured data
  const tableSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: name,
    description: about,
    url: pageUrl,
    itemListElement: validRows.map((row, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Thing',
        name: row.cells[0] || '', // First cell is the feature name
        description: `${columns[0]}: ${row.cells[0] || ''}; ${columns[1]}: ${row.cells[1] || ''}; ${columns[2]}: ${row.cells[2] || ''}`,
        // Additional properties for each column value
        additionalProperty: columns.slice(1).map((col, colIndex) => ({
          '@type': 'PropertyValue',
          name: col,
          value: row.cells[colIndex + 1] || '',
        })),
      },
    })),
  };

  return <JSONLDScript data={tableSchema} id="table-structured-data" />;
}
